<div class="cart">

  @if (cartService.isLoading() || cartService.productsLoading()) {
    <div class="cart-preloader-container">
      <preloader class="cart-preloader" />
    </div>
  }

  @if (cartService.isCartInit()) {
    @let errorMessage = cartService.initCartError() || cartService.productsError() || error();
    @if (errorMessage) {
      <div class="cart-error">
        {{ errorMessage }}
      </div>
    }

    @if (!cartService.cartItems().length) {
      @if (!errorMessage) {
        <div class="no-items">
          {{ 'cart.empty' | translate }}
        </div>
      }
      <div class="no-items-delimiter"></div>
    } @else {
      <div class="cart-items">
        @let hasOutOfStock = outOfStockIndex() !== -1;
        @for (cartItem of cartService.inAndOutOfStockCartItemsSorted(); track cartItem.productId) {
          @let product = getProduct(cartItem.productId);
          @let itemCostWithDiscount = getItemCostWithDiscount(product);
          @let activePriceIndex = getActivePriceIndex(product);

          @if (hasOutOfStock && $index === outOfStockIndex()) {
            <div class="cart-out-of-stock-heading">
              {{ 'cart.out_of_stock_items' | translate }}
            </div>
          }

          @if (product && cartItem) {
            <div class="cart-item" [class.cart-item--out-of-stock]="!cartItem.isInStock">
              <div class="cart-item-column">
                <div class="cart-item-row cart-item-row-info">
                  <div class="cart-item-img-container">
                    <img
                      class="cart-item-img"
                      [src]="getItemImg(product)"
                      [alt]="product.name"
                      loading="lazy"
                    >
                  </div>

                  <div class="cart-item-content">
                    <a
                      class="cart-item-name g-main-link"
                      [routerLink]="['/', product.slug]"
                      routerLinkWithLang
                    >
                      {{ product.name }}
                      @if (product.isLargeSized) {
                        <span class="cart-item-large-size-label"> (крупногабаритн.)</span>
                      }
                    </a>

                    <div class="cart-item-sku">
                      {{ 'global.sku' | translate }}: {{ product.sku }}
                    </div>

                    @if (cartItem.isInStock) {
                      @if (cartItem.errorMessage; as errorMessage) {
                        <div class="cart-item-error">
                          {{ errorMessage }}
                        </div>
                      }
                    } @else {
                      <div class="cart-item-out-of-stock-badge">
                        {{ 'cart.out_of_stock' | translate }}
                      </div>
                    }
                  </div>
                </div>
              </div>

              <div class="cart-item-column">
                <div class="cart-item-row cart-item-row-cost">
                  <div
                    class="cart-item-price-container"
                    [class.cart-item-price-container-hidden]="cartItem.isInStock && product.price === cartItem.cost && activePriceIndex === undefined"
                  >
                    <div
                      class="cart-item-price"
                      [class.cart-item-price-inactive]="cartItem.isInStock && activePriceIndex !== undefined"
                    >
                      {{ product.price }} {{ 'global.uah' | translate }}
                    </div>
                    @if (product.oldPrice) {
                      <div class="cart-item-old-cost">
                        {{ product.oldPrice }}
                      </div>
                    }
                  </div>

                  @if (cartItem.isInStock) {
                    <div class="cart-item-qty-container">
                      <quantity-control
                        class="cart-item-qty"
                        [initialValue]="cartItem.qty"
                        (valueChanged)="onQtyChange($index, $event)"
                      />
                    </div>
                    <div class="cart-item-cost-container">
                      <div class="cart-item-cost cart-item-cost-no-discount">
                        @if (!itemCostWithDiscount) {
                          <div
                            class="cart-item-cost"
                            [class.cart-item-cost-special]="cartItem.oldCost"
                          >
                            {{ cartItem.cost }} {{ 'global.uah' | translate }}
                          </div>
                        }
                        @if (cartItem.oldCost) {
                          <div class="cart-item-old-cost">
                            {{ cartItem.oldCost }} {{ 'global.uah' | translate }}
                          </div>
                        }
                      </div>
                      @if (itemCostWithDiscount) {
                        <div class="cart-item-cost cart-item-cost-discount">
                          {{ itemCostWithDiscount }} {{ 'global.uah' | translate }}
                        </div>
                      }
                    </div>
                  }
                </div>

                @if (cartItem.isInStock && !product.oldPrice) {
                  <product-discounts
                    class="cart-item-discounts"
                    [discountedPrices]="product.discountedPrices"
                    [activeIndex]="activePriceIndex"
                    [isCompact]="true"
                  />
                }

                <button
                  class="cart-item-actions g-main-link g-main-link-black"
                  [matMenuTriggerFor]="actionsMenu"
                  type="button"
                >
                  <tc-icon name="ellipsis" />
                </button>

                <mat-menu #actionsMenu="matMenu" xPosition="before">
                  <button
                    class="cart-item-action"
                    (click)="deleteFromCart(product)"
                    type="button"
                  >
                    <tc-icon
                      class="cart-item-action-icon"
                      name="trash"
                    />

                    {{ 'checkout.delete' | translate }}
                  </button>
                  <button
                    class="cart-item-action"
                    (click)="moveCartItemToWishlist(product)"
                    type="button"
                  >
                    <tc-icon
                      class="cart-item-action-icon"
                      name="heart"
                    />

                    {{ 'checkout.add_to_wishlist' | translate }}
                  </button>
                </mat-menu>

                @if (cartItem.isInStock && cartItem.additionalServiceIds?.length) {
                  <div class="cart-item-buy-info">
                    <div class="cart-item-row">
                      @for (serviceId of cartItem.additionalServiceIds; track serviceId) {
                        @let service = getAdditionalService(serviceId);
                        <div class="cart-item-additional-service">
                          {{ service?.name }} (+{{ service?.price }} {{ 'global.uah' | translate }})
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        }
      </div>

      <div class="cart-info">
        <div class="cart-prices">
          @if (cartService.cartPrices()?.discountValue; as discountValue) {
            <div class="cart-price">
              <div class="cart-price-label">{{ 'global.items_cost' | translate }}:</div>
              <div class="cart-price-value">{{ cartService.cartPrices()?.itemsCost }} {{ 'global.uah' | translate }}</div>
            </div>
            <div class="cart-price">
              <div class="cart-price-label">
                {{ 'global.discount' | translate }}:
              </div>
              <div class="cart-price-value cart-price-value-discount">-{{ discountValue }} {{ 'global.uah' | translate }}</div>
            </div>
          }
          <div class="cart-price cart-price-total">
            <div class="cart-price-label">{{ 'global.total_cost' | translate }}:</div>
            <div class="cart-price-value cart-price-value-total">{{ cartService.cartPrices()?.totalCost }} {{ 'global.uah' | translate }}</div>
          </div>
        </div>
        <div class="cart-btns">
          @if (canPlaceOrder()) {
            <a
              class="cart-btn cart-btn-checkout g-btn g-btn-default"
              routerLink="/checkout"
              routerLinkWithLang
              (click)="checkout()"
            >
              {{ 'cart.checkout' | translate }} <i class="cart-btn-icon mbi mbi-arrow-right"></i>
            </a>
          } @else {
            <div class="cart-btn cart-cost-error">
              {{ 'global.minimal_order_cost' | translate }}: {{ minimalOrderCost }} {{ 'global.uah' | translate }}.
            </div>
          }
          <button
            class="cart-btn cart-btn-continue g-btn"
            (click)="continueShopping.emit()"
          >
            {{ 'cart.resume_shopping' | translate }}
          </button>
        </div>
      </div>

      @if (cartService.crossSellProductIds().length) {
        <cross-sell-products
          class="cross-sell"
          [productIds]="cartService.crossSellProductIds()"
          (showCart)="onCrossSellShowCart($event)"
        />
      }
    }
  } @else {
    <div class="cart-loading-placeholder"></div>
  }

</div>
